import { UserFacingError, responseError, isCancellation } from "@streamoid/errors";
/**
 * Parses the JSON returned by a network request
 *
 * @param  {object} response A response from a network request
 *
 * @return {object}          The parsed JSON from the request
 */
function parseJSON(response) {
  //   console.log("response", response);
  if (response.status === 204 || response.status === 205) {
    return null;
  }
  return response.json();
}

/**
 * Checks if a network request came back fine, and throws an error if not
 *
 * @param  {object} response   A response from a network request
 *
 * @return {object|undefined} Returns either the response, or throws an error
 */
/** A W3C trace context header for a request the browser is about to make.
 *
 *  Minting it here rather than letting the server start the trace is what joins
 *  a user's session to whatever the request touched: the server's
 *  auto-instrumentation continues this trace id rather than generating its own,
 *  so the id the browser knows IS the server's trace id, and the error
 *  reference the user is shown derives from it.
 *
 *  `00` is the version, `01` the sampled flag. Both are fixed for us: sampling
 *  decisions are made server-side and a request we did not record is a request
 *  we cannot explain.
 */
function newTraceparent() {
  const random = (bytes) => {
    const buffer = new Uint8Array(bytes);
    (globalThis.crypto || {}).getRandomValues?.(buffer);
    return Array.from(buffer, (b) => b.toString(16).padStart(2, "0")).join("");
  };
  const traceId = random(16);
  const spanId = random(8);
  // All-zero ids are invalid trace context and are dropped silently by
  // collectors, so a missing crypto implementation must not produce one.
  if (/^0+$/.test(traceId) || /^0+$/.test(spanId)) return null;
  return `00-${traceId}-${spanId}-01`;
}

/**
 * Requests a URL, returning a promise
 *
 * @param  {string} url       The URL we want to request
 * @param  {object} [options] The options we want to pass to "fetch"
 *
 * @return {object}           The response data
 */
export default async function request(url, options = {}) {
  const traceparent = newTraceparent();
  const headers = new Headers(options.headers);
  if (traceparent) headers.set("traceparent", traceparent);
  const withTrace = { ...options, headers };
  let response;
  try { response = await fetch(url, withTrace); }
  catch (cause) {
    if (isCancellation(cause)) throw cause;
    throw new UserFacingError(cause, { method: options.method || "GET" });
  }
  if (!response.ok) throw await responseError(response, { method: options.method || "GET" });
  try { return await parseJSON(response); }
  catch (cause) { throw new UserFacingError(cause, { method: options.method || "GET", status: response.status }); }
}

export { newTraceparent };
